import { createAsyncThunk, createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { Product } from "../types/store";
import api from "../lib/axios";
import type { ProductFormValues } from "../components/Forms/AddProductModal";
import { toast } from "react-toastify";
import type { SalesHistoryFilters } from "./salesSlice";
import type { ProductHistory } from "../types/sales";
import type { RootState } from "../store";

export interface ProductState {
  products: Product[];
  productHistory: ProductHistory[];
  historyFilters: SalesHistoryFilters | null;
  loading: boolean;
  error: string | null;
  success: boolean | null;
}

export interface CreateProductArgs {
  products: ProductFormValues[];
  store_id: string;
}

const initialState: ProductState = {
  products: [],
  productHistory: [],
  historyFilters: null,
  loading: false,
  error: null,
  success: null,
};

interface RestockData {
  product_id: string;
  quantity: number;
  mfg_date?: string | null;
  exp_date?: string | null;
}

// Response structure matches your controller
interface RestockResponse {
  message: string;
  data: (Product & {
    newStoreItem: {
      id: string;
      quantity: number;
      mfg_date: string | null;
      exp_date: string | null;
    };
  })[];
}

// === Thunks ===

// Fetch all products
export const fetchProducts = createAsyncThunk<Product[], string | undefined>(
  "products/fetchAll",
  async (storeId, { rejectWithValue }) => {
    try {
      const res = await api.get("/product/all", {
        params: storeId ? { store_id: storeId } : {},
      });
      return res.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch products");
    }
  }
);

// Create a new product
export const createProduct = createAsyncThunk<Product[], CreateProductArgs>(
  "products/create",
  async ({products: newProducts, store_id}, { rejectWithValue }) => {
    try {
      const res = await api.post(`/product/add?store_id=${store_id}`, newProducts);
      toast.success("Added successfully");
      return res.data.data; 
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to create product");
      return rejectWithValue(error.response?.data?.message || "Failed to create product");
    }
  }
);

// Restock items
export const restockProducts = createAsyncThunk<
  RestockResponse,
  RestockData[],
  { rejectValue: string }
>(
  "products/restockProducts",
  async (restocks, { rejectWithValue }) => {
    try {
      const response = await api.post("/product/restock", { restock: restocks });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to restock products."
      );
    }
  }
);

// Archive products
export const archiveProducts = createAsyncThunk<
  { count: number },
  string[],
  { rejectValue: string }
>(
  "products/archive",
  async (product_ids, { rejectWithValue }) => {
    try {
      const res = await api.put("/product/archive", { product_ids });
      return res.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to archive products");
    }
  }
);

// Unarchive products
export const unarchiveProducts = createAsyncThunk<
  { count: number },
  string[],
  { rejectValue: string }
>(
  "products/unarchive",
  async (product_ids, { rejectWithValue }) => {
    try {
      const res = await api.put("/product/unarchive", { product_ids });
      return res.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to unarchive products");
    }
  }
);

// Fetch product history
export const fetchProductHistory = createAsyncThunk<
  ProductHistory[],
  void,
  { state: RootState, rejectWithValue: string }
>("products/getProductHistory", async  (_, { getState, rejectWithValue}) => {
  try {
    const state = getState();
    const filters = state.products.historyFilters;

    const params = new URLSearchParams();

    if (filters?.storeId) {
      params.append("storeId", filters.storeId);
    }

    if (filters?.productId) {
      params.append("productId", filters.productId);
    }

    if (filters?.startDate) {
      params.append("startDate", filters.startDate);
    }

    if (filters?.endDate) {
      params.append("endDate", filters.endDate);
    }

    const queryString = params.toString();
    const url = queryString
      ? `/product/history?${queryString}`
      : `/product/history`;

    const { data } = await api.get(url);

    // console.log("Product history data: ", data.data);

    return data.data as ProductHistory[];

  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || error.message || "Failed to fetch product history")
  }
});

// === Slice ===
const productSlice = createSlice({
  name: "products",
  initialState,
  reducers: {
    updateHistoryFilters: (state, action: PayloadAction<SalesHistoryFilters>) => {
      state.historyFilters = action.payload;
    }
  },
  extraReducers: (builder) => {
    // Fetch products
    builder
      .addCase(fetchProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProducts.fulfilled, (state, action: PayloadAction<Product[]>) => {
        state.loading = false;
        console.log("Slice Products: ", action.payload);
        console.log("Slice Loading: ", state.loading);
        state.products = action.payload;
        state.success = true;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.success = false;
    });
    
    // Create product
    builder
    .addCase(createProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
    })
    .addCase(createProduct.fulfilled, (state, action: PayloadAction<Product[]>) => {
        state.loading = false;
        state.success = true;
        state.products = [...action.payload, ...state.products];
    })
    .addCase(createProduct.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        state.error = action.payload as string;
      });

    // Restock product
    builder
      .addCase(restockProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(restockProducts.fulfilled, (state, action) => {
        state.loading = false;

        // Update the affected products in state
        action.payload.data.forEach((updatedProduct) => {
          const index = state.products.findIndex(
            (p) => p.id === updatedProduct.id
          );
          if (index !== -1) {
            state.products[index] = {
              ...state.products[index],
              ...updatedProduct,
            };
          } else {
            state.products.unshift(updatedProduct);
          }
        });
      })
      .addCase(restockProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to restock products.";
      });

      // Archive products — remove them from active list
      builder
        .addCase(archiveProducts.fulfilled, (state, action) => {
          const archivedIds = new Set(action.meta.arg);
          state.products = state.products.filter((p) => !archivedIds.has(p.id));
        });

      // Unarchive products — fetchProducts is dispatched inside the thunk to refresh
      builder
        .addCase(unarchiveProducts.fulfilled, (_state, _action) => {});

      // Fetch product history
      builder.addCase(fetchProductHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProductHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.productHistory = action.payload;
      })
       .addCase(fetchProductHistory.rejected, (state, action) => {
        state.loading = false;
        console.log("Error: ", action.payload);
        state.error = "Failed to fetch product history. Please try again";
      });
  },
});

export const { updateHistoryFilters } = productSlice.actions;
export const productReducer = productSlice.reducer;
